/**
 * withStyles HOC shim.
 *
 * isomorphic-style-loader v5 dropped the v4 HOC in favour of a hook
 * (`useStyles`). Converting every class component in the app to a
 * function component is out of scope for the webpack migration, so
 * this file reproduces the v4 HOC signature on top of v5's loader.
 *
 * Integration points:
 * - App.js exposes `insertCss` via legacy React context (childContextTypes).
 * - client.js and src/apiRoutes/reactRoute.js supply the `insertCss` callback
 *   that calls each style module's `_insertCss()` method — the same API in
 *   v4 and v5.
 *
 * Usage is unchanged at the call sites:
 *   import withStyles from 'utils/withStyles';
 *   import s from './Foo.css';
 *   class Foo extends React.Component { ... }
 *   export default withStyles(s)(Foo);
 */
import React from 'react';
import PropTypes from 'prop-types';

export default function withStyles(...styles) {
  return function wrapWithStyles(ComposedComponent) {
    class WithStyles extends React.Component {
      static contextTypes = {
        insertCss: PropTypes.func.isRequired,
      };

      // Run on mount on the server so SSR collects the CSS before
      // renderToString finishes. UNSAFE_ prefix avoids the deprecation
      // warning in React 16.3+.
      // eslint-disable-next-line camelcase
      UNSAFE_componentWillMount() {
        this.removeCss = this.context.insertCss(...styles);
      }

      componentWillUnmount() {
        if (typeof this.removeCss === 'function') {
          setTimeout(this.removeCss, 0);
        }
      }

      render() {
        return <ComposedComponent {...this.props} />;
      }
    }

    const name =
      ComposedComponent.displayName || ComposedComponent.name || 'Component';
    WithStyles.displayName = `WithStyles(${name})`;
    return WithStyles;
  };
}
